import { useState } from 'react'
import { Star } from 'lucide-react'
import { RAISON_PAS_GESTION, validerCheminFavori } from '@gestion/shared/favoris'
import { useToast } from '../admin/primitives.js'
import { titreDePageCourant } from '../prefs.js'
import { cheminDe, titleForPath } from '../navigation/site-navigation.js'
import { ajouterFavori, nomDeFavori, reessayerFavoris, retirerFavori, useFavoris } from './favoris.js'

/**
 * L'ÉTOILE DE L'EN-TÊTE (lot 5, 24/10/2026) : « Ajouter aux favoris » la page
 * affichée, ou l'en « Retirer ». L'adresse entière est épinglée, paramètres
 * compris (décision d'Hugo) ; jamais l'ancre.
 *
 * Le nom est le titre que la page a posé (`titreDePageCourant` : une soirée,
 * une association, un signalement le posent eux-mêmes), sinon celui du
 * registre. C'est un instantané : renommer la soirée plus tard ne renomme pas
 * le favori.
 *
 * Indisponible, l'étoile reste focalisable (aria-disabled) et dit pourquoi,
 * au survol comme au toucher : la raison du validateur partagé (« Un incident
 * ne s’ajoute pas aux favoris. »), jamais « Seule une page de Gestion… » sur
 * une page de Gestion. Après une première lecture manquée, le clic relit
 * aussi la liste (`reessayerFavoris`). Pas de confirmation : le geste se
 * défait, et le retrait offre « Annuler ».
 *
 * L'adresse arrive en prop : le shell la connaît déjà, et un import de
 * `useMe` ferait une boucle entre ce fichier et AppShell.
 */
export function BoutonFavori(props: { adresse: string }) {
  const toast = useToast()
  const { statut, liste, max } = useFavoris()
  const [occupe, setOccupe] = useState(false)
  const adresse = props.adresse.split('#', 1)[0] ?? props.adresse
  const favori = liste.find((f) => f.path === adresse)
  const epingle = favori !== undefined

  const verdict = validerCheminFavori(adresse)
  const raison = !verdict.ok ? (verdict.raison === RAISON_PAS_GESTION ? 'Cette page ne peut pas être ajoutée aux favoris.' : verdict.raison)
    : statut !== 'pret' ? 'Favoris indisponibles pour l’instant.'
      : !epingle && liste.length >= max ? `${max} favoris au plus : retirez-en un pour ajouter cette page.`
        : null
  const libelle = epingle ? 'Retirer des favoris' : 'Ajouter aux favoris'

  const agir = async () => {
    // Première lecture manquée : le clic relit la liste. Un clic, jamais un
    // effet : un nouvel échec ne relance rien de lui-même.
    if (statut === 'erreur') {
      reessayerFavoris()
      if (verdict.ok) { toast('error', 'Favoris indisponibles pour l’instant : nouvelle lecture en cours.'); return }
    }
    if (raison) { toast('error', raison); return }
    if (occupe) return
    setOccupe(true)
    try {
      if (favori) await retirerFavori(favori, toast)
      else {
        const repli = cheminDe(adresse) === '/' ? 'Tableau de bord' : (titleForPath(adresse) ?? 'Page de Gestion')
        await ajouterFavori({ path: adresse, label: nomDeFavori(titreDePageCourant(), repli) }, toast)
      }
    } finally {
      setOccupe(false)
    }
  }

  return (
    <button
      type="button"
      className={`lc-favbtn${epingle ? ' lc-favbtn--on' : ''}`}
      aria-pressed={epingle}
      aria-label={libelle}
      aria-disabled={raison || occupe ? 'true' : undefined}
      title={raison ?? libelle}
      onClick={() => { void agir() }}
    >
      <Star size={18} aria-hidden="true" fill={epingle ? 'currentColor' : 'none'} />
    </button>
  )
}
